import dayjs, { Dayjs } from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isYesterday from 'dayjs/plugin/isYesterday'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { thru } from 'lodash'
import railsTimezone from 'rails-timezone'

import { useSessionProvider } from '@shared/composables/use-session-provider'
import { DateFormats } from '@shared/constants/date-form-format'
import { centralTimeDayJs } from '@shared/constants/time-zone'
import { DayPeriod } from '@shared/models/enums/day-periods'
import { TimeType } from '@shared/models/enums/time-type'
import { Nullable } from '@shared/models/types/primary'
import { getTimezoneWithDefault } from '@shared/utils/helpers/timezone'

import { UsageLimitTimeStep } from '@protos/usage_limit_time_step_pb'

dayjs.extend(customParseFormat)
dayjs.extend(isSameOrBefore)
dayjs.extend(isYesterday)
dayjs.extend(relativeTime)
dayjs.extend(advancedFormat)
dayjs.extend(weekOfYear)
dayjs.extend(utc)
dayjs.extend(timezone)

export interface DateTimeFormatParams {
  date?: string | Date | Dayjs | bigint
  formatPattern?: string
  fallbackValue?: string
  forceTimezone?: string
}

export const forceTimezone = (date: Dayjs, timezone: string) => {
  try {
    return date.tz(timezone)
  } catch {
    try {
      return date.tz(railsTimezone.from(timezone))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error while trying to force timezone', error)
      return date
    }
  }
}

export const getDateTimeString = (params: DateTimeFormatParams): string => {
  if (!params.date) {
    return params.fallbackValue || '-'
  }

  const dateInstance = thru(getTransformedDate(params.date), (date) =>
    params.forceTimezone ? forceTimezone(date, params.forceTimezone) : date
  )

  return thru(dateInstance, (date) =>
    params.formatPattern ? date.format(params.formatPattern) : date.toString()
  )
}

export const isMoreThanDuration = (
  date: string,
  newDate: string,
  durationInMinutes: number
) => {
  return (
    Math.abs(dayjs(date).diff(dayjs(newDate), 'minute')) > durationInMinutes
  )
}

export const getAgeNumber = (date: string | Date, fallback = '-') => {
  const dob = dayjs(date)

  if (!dob?.isValid()) {
    return fallback
  }

  return dayjs().diff(dob, 'y')
}

export const isIsoUTCDate = (date: string) => {
  const isoDateRegex = /[TZ]/
  return isoDateRegex.test(date)
}

export const getTransformedDate = (
  date: string | Date | Dayjs | bigint
): Dayjs => {
  return typeof date === 'string' ? dayjs(date) : dayjs(date.toString())
}

export const convertUnixTimestampToDate = (
  timestamp: bigint | string | number
): Date | Dayjs => {
  return dayjs.unix(parseInt(timestamp as unknown as string, 10)).toDate()
}

export const getNow = () => dayjs()
export const yesterday = () => dayjs().subtract(1, 'day')
export const getFirstDayOfCurrentMonth = () => dayjs().startOf('month')

export const convertBeforeUpload = (endDate: string) => {
  if (!endDate) {
    return ''
  }

  return getTransformedDate(endDate)
    .add(1, 'day')
    .subtract(1, 'ms')
    .toDate()
    .toString()
}

export const isValidDate = (date: string) => getTransformedDate(date).isValid()

export const getTimeFromString = (time: string) => {
  const isPM = time.includes('PM')
  const timeString = time.split(' ')[0]
  const timeElements = timeString.split(':')
  const hour = isPM ? Number(timeElements[0]) + 12 : Number(timeElements[0])
  const minute = Number(timeElements[1])

  return {
    hour,
    minute,
  }
}

export const isToday = (date: dayjs.Dayjs) => {
  return date.isSame(dayjs(), 'day')
}

export const isPastDate = (time: Date) => {
  return !dayjs(time).isAfter(yesterday())
}

export const startAfter = (start: string, end: string) => {
  const startDate = dayjs(start)
  const endDate = dayjs(end)
  return endDate.from(startDate)
}

export const getDatesInRangeByPeriod = (
  startDate: string,
  endDate: string,
  timeStep: UsageLimitTimeStep
) => {
  let currentDate = dayjs(startDate)
  const end = dayjs(endDate)
  const dateArray = []

  while (currentDate.isSameOrBefore(end)) {
    if (timeStep === UsageLimitTimeStep.DAY) {
      dateArray.push(currentDate.format('YYYY-MM-DD'))
      currentDate = currentDate.add(1, 'day')
    } else if (timeStep === UsageLimitTimeStep.HOUR) {
      for (let hour = 0; hour < 24; hour++) {
        dateArray.push(currentDate.hour(hour).format('YYYY-MM-DD HH:00'))
      }
      currentDate = currentDate.add(1, 'day')
    } else if (timeStep === UsageLimitTimeStep.WEEK) {
      dateArray.push(currentDate.format('YYYY-MM-DD'))
      currentDate = currentDate.add(7, 'day')
    } else if (timeStep === UsageLimitTimeStep.MONTH) {
      dateArray.push(currentDate.format('YYYY-MM'))
      currentDate = currentDate.add(1, 'month')
    } else if (timeStep === UsageLimitTimeStep.YEAR) {
      dateArray.push(currentDate.format('YYYY'))
      currentDate = currentDate.add(1, 'year')
    }
  }

  return dateArray
}

export const getWeekAndYearString = (date: string) => {
  return dayjs(date).format('ww-YYYY')
}

export const getPastDaysInCurrentMonth = () => {
  const today = dayjs()
  const firstDayOfMonth = dayjs().startOf('month')
  const days = today.diff(firstDayOfMonth, 'day')

  return days
}

export const getEndOfTheDay = (date: string): string => {
  return dayjs(date).endOf('day').toISOString()
}

export enum OperationType {
  AddOperation = 'addOperation',
  SubtractOperation = 'subtractOperation',
}

export function operateTime(
  time: string,
  duration: string,
  operationType: OperationType
) {
  const operations = {
    [OperationType.AddOperation]: (
      timeDayjs: dayjs.Dayjs,
      hours: number,
      minutes: number
    ) => timeDayjs.add(hours, 'hour').add(minutes, 'minute'),
    [OperationType.SubtractOperation]: (
      timeDayjs: dayjs.Dayjs,
      hours: number,
      minutes: number
    ) => timeDayjs.subtract(hours, 'hour').subtract(minutes, 'minute'),
  }

  const timeDayjs = dayjs()
    .set('hour', parseInt(time.split(':')[0], 10))
    .set('minute', parseInt(time.split(':')[1], 10))

  const [hours, minutes] = duration.split(':').map(Number)

  const resultTime = operations[operationType](timeDayjs, hours, minutes)

  return resultTime.format('HH:mm')
}

export const calculateAge = (dob: string) => {
  const birthDate = new Date(dob)
  const today = new Date()

  let age = today.getFullYear() - birthDate.getFullYear()

  const monthDifference = today.getMonth() - birthDate.getMonth()
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--
  }

  return age
}

export const formatDate = (date: string, format: string) => {
  return dayjs(date).format(format)
}

export const isTimeValid = (
  timeStr: string,
  timeFormat = DateFormats.Time,
  dayPeriod = DayPeriod.AM
): boolean => {
  const timeStrWithDayPeriod = `${timeStr} ${dayPeriod}`
  const time = dayjs(
    timeFormat === DateFormats.TimeAM ? timeStrWithDayPeriod : timeStr,
    timeFormat,
    true
  )

  return time.isValid()
}

export const calculateEndTime = (
  startDate: string,
  startTime: string,
  dayPeriod: string,
  duration: number,
  timeType: TimeType
): Nullable<string> => {
  if (!startDate || !startTime || !isTimeValid(startTime, DateFormats.Time)) {
    return null
  }
  const dateTime = new Date(`${startDate} ${startTime} ${dayPeriod}`)

  if (!dateTime) {
    return null
  }

  const minutesOffset = {
    [TimeType.Minutes]: Number(duration),
    [TimeType.Hours]: Number(duration) * 60,
    [TimeType.Days]: Number(duration) * 60 * 24,
  }[timeType]

  dateTime.setMinutes(dateTime.getMinutes() + minutesOffset)

  return dateTime.toISOString()
}

export const getDateAndTimeFromISOString = (date: string) => {
  if (!date) {
    return {
      date: '',
      time: '',
    }
  }
  const dateTime = dayjs(date)
  return {
    date: dateTime.format('MM/DD/YYYY'),
    time: dateTime.format('HH:mm'),
  }
}

export type PreferableTimeType = Exclude<TimeType, TimeType.Minutes>
export type TimeTypeOptions = {
  preferType?: PreferableTimeType | PreferableTimeType[]
  excludeType?: PreferableTimeType | PreferableTimeType[]
}
// Defaults to minutes if preferred type can't be calculated
export const getDurationAndTimeTypeFromISOStrings = (
  startTime: string,
  endTime: string,
  options: TimeTypeOptions = {}
) => {
  const startDateTime = dayjs(startTime)
  const endDateTime = dayjs(endTime)

  if (!endTime || !startTime || endDateTime.isBefore(startDateTime)) {
    return {
      duration: 0,
      timeType: TimeType.Minutes,
    }
  }

  const duration = endDateTime.diff(startDateTime, 'minutes')

  const typePreferred = (type: PreferableTimeType) => {
    const { preferType, excludeType } = options

    if (preferType) {
      return preferType === type || preferType.includes(type)
    }

    if (excludeType) {
      return excludeType !== type || !excludeType.includes(type)
    }

    return true
  }

  if (typePreferred(TimeType.Days) && duration && duration % 1440 === 0) {
    return {
      duration: duration / 1440,
      timeType: TimeType.Days,
    }
  }

  if (typePreferred(TimeType.Hours) && duration && duration % 60 === 0) {
    return {
      duration: duration / 60,
      timeType: TimeType.Hours,
    }
  }

  return {
    duration,
    timeType: TimeType.Minutes,
  }
}

export const convertToAmPm = (militaryTime: string) => {
  const hours = parseInt(militaryTime.slice(0, 2), 10)
  const minutes = militaryTime.slice(3, 5)

  const meridiem = hours >= 12 ? DayPeriod.PM : DayPeriod.AM

  let amPmHours = hours % 12
  amPmHours = amPmHours === 0 ? 12 : amPmHours

  const amPmTime = `${amPmHours}:${minutes}`

  const time = dayjs(amPmTime, 'H:mm', true).format(DateFormats.Time)

  return { time, meridiem }
}

export const createDateFromMonthYear = (monthYear: string) => {
  const [month, year] = monthYear.split(' ')
  return new Date(`${month} 1, ${year}`)
}

export const getTimeToDate = (date: string) => dayjs().to(dayjs(date))

export const formattedDate = (date: string, formatPattern?: string) => {
  if (date === '-') {
    return date
  }
  const { currentGroupTimezone } = useSessionProvider()

  return getDateTimeString({
    date,
    formatPattern: formatPattern ?? 'MMM DD YYYY, HH:mm:ss',
    forceTimezone:
      getTimezoneWithDefault(currentGroupTimezone.value) || centralTimeDayJs,
    fallbackValue: '',
  })
}

export const formattedTime = (date: string) => {
  const { currentGroupTimezone } = useSessionProvider()

  const params: DateTimeFormatParams = {
    date: date,
    formatPattern: 'h:mm A',
    fallbackValue: '',
    forceTimezone:
      getTimezoneWithDefault(currentGroupTimezone.value) || centralTimeDayJs,
  }

  return getDateTimeString(params)
}

export function getLastSecondOfMonth(date: Date): string {
  return dayjs(date).endOf('month').toISOString()
}

export function getFirstSecondOfMonth(date: Date): string {
  return dayjs(date).startOf('month').toISOString()
}

export function isCurrentMonth(date: Date): boolean {
  const now = dayjs() // Get the current date

  // Check if the year and the month of the given date are the same as the current date
  return dayjs(date).isSame(now, 'month')
}
